import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import axios from '@/utils/api';
import { API } from '@/utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';

export default function PublisherCampaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyingId, setApplyingId] = useState(null);

  useEffect(() => {
    fetchMarketplace();
  }, []);

  const fetchMarketplace = async () => {
    try {
      const response = await axios.get(`${API}/publisher/marketplace`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to load campaign marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async (e, campaignId) => {
    e.stopPropagation();
    setApplyingId(campaignId);
    try {
      await axios.post(`${API}/publisher/campaigns/${campaignId}/apply`);
      setCampaigns(prev =>
        prev.map(c => c.id === campaignId ? { ...c, has_applied: true, application_status: 'pending' } : c)
      );
      toast.success('Application submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplyingId(null);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  if (loading) {
    return (
      <DashboardLayout role="publisher">
        <div className="p-8">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="publisher">
      <div className="p-8" data-testid="publisher-campaigns-page">
        <h1 className="font-heading text-4xl font-bold mb-2">Campaign Marketplace</h1>
        <p className="text-muted-foreground mb-8">Active campaigns matching your content categories</p>

        {campaigns.length === 0 ? (
          <div className="border border-foreground p-12 text-center">
            <p className="text-muted-foreground">No matching campaigns available right now. Check back soon.</p>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            data-testid="marketplace-grid"
          >
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="group flex flex-col rounded-xl border border-border bg-white overflow-hidden transition-all duration-200 hover:shadow-lg hover:border-foreground/30"
                data-testid={`marketplace-card-${campaign.id}`}
              >
                {/* Clickable area — image + content */}
                <div
                  className="cursor-pointer"
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                >
                  {/* Image */}
                  <div className="relative aspect-[16/10] bg-neutral-100 overflow-hidden">
                    {campaign.campaign_image_url ? (
                      <img
                        src={`${BACKEND}${campaign.campaign_image_url}`}
                        alt={campaign.name}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div
                      className={`${campaign.campaign_image_url ? 'hidden' : 'flex'} h-full w-full items-center justify-center bg-neutral-200`}
                    >
                      <span className="text-3xl font-bold text-neutral-400" data-testid="placeholder-initials">
                        {getInitials(campaign.brand_name)}
                      </span>
                    </div>
                    {/* Available badge */}
                    <span
                      className="absolute left-3 top-3 rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold text-white shadow-sm"
                      data-testid="available-badge"
                    >
                      Available
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-4 pb-2">
                    {/* Brand */}
                    <div className="mb-2 flex items-center gap-2">
                      {campaign.brand_logo_url ? (
                        <img
                          src={campaign.brand_logo_url}
                          alt={campaign.brand_name}
                          className="h-5 w-5 rounded-full object-contain"
                        />
                      ) : null}
                      <span className="text-xs font-medium text-muted-foreground">{campaign.brand_name}</span>
                    </div>

                    {/* Title */}
                    <h3 className="font-bold text-base leading-tight mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                      {campaign.name}
                    </h3>

                    {/* Description excerpt */}
                    {campaign.description && (
                      <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                        {campaign.description}
                      </p>
                    )}

                    {/* Earning model */}
                    {campaign.earning_model && (
                      <div
                        className="rounded-md bg-neutral-50 border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-neutral-700"
                        data-testid="earning-model"
                      >
                        {campaign.earning_model}
                      </div>
                    )}
                  </div>
                </div>

                {/* Apply button — sits outside the clickable navigation area */}
                <div className="px-4 pb-4 pt-2 mt-auto">
                  {campaign.has_applied ? (
                    <div
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 py-2 text-sm font-medium text-emerald-700"
                      data-testid={`applied-badge-${campaign.id}`}
                    >
                      <CheckCircle size={16} weight="fill" />
                      {campaign.application_status === 'approved' ? 'Approved' : 'Applied'}
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={(e) => handleApply(e, campaign.id)}
                      disabled={applyingId === campaign.id}
                      className="w-full bg-[#DC120F] text-white hover:bg-[#b80f0d]"
                      data-testid={`apply-btn-${campaign.id}`}
                    >
                      <PaperPlaneTilt size={16} className="mr-1.5" weight="bold" />
                      {applyingId === campaign.id ? 'Applying...' : 'Apply'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
